"use client"

import { motion, AnimatePresence } from "framer-motion"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"

interface ModalProps {
    isOpen: boolean
    onClose: () => void
    children: React.ReactNode
    className?: string
    zIndex?: number
}

export function Modal({ isOpen, onClose, children, className, zIndex = 50 }: ModalProps) {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = "unset"
        }
        return () => {
            document.body.style.overflow = "unset"
        }
    }, [isOpen])

    // Use Portal if possible, but basic rendering works for now in Next.js app directory usually
    // Adding separate portal handling can be tricky with hydration, so I'll stick to inline presence
    // but with fixed z-index it acts like a portal visually.

    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    if (!mounted) return null

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-all h-[100dvh] w-screen"
                        style={{ zIndex }}
                    />

                    {/* Dialog */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-6 pointer-events-none"
                        style={{ zIndex }}
                    >
                        <div className={cn("relative w-full mx-auto pointer-events-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] sm:max-h-[95vh] overflow-y-auto", className)}>
                            <button
                                onClick={onClose}
                                className="absolute right-3 top-3 z-[100] rounded-full bg-white p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all cursor-pointer group hover:scale-110 active:scale-90"
                                aria-label="Close modal"
                            >
                                <X className="h-4 w-4 transition-transform group-hover:rotate-90" />
                            </button>
                            {children}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    )
}
